import { AnimatePresence, motion } from "framer-motion";
import { Bell, Briefcase, CheckCheck, ExternalLink, Globe, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../../lib/api";
import { timeAgo } from "../../lib/format";

const TYPE_ACCENT = {
    new_candidate: "bg-blue-50 text-blue-700 border-blue-200",
    pepite_detected: "bg-amber-50 text-amber-800 border-amber-200",
    urgent_call: "bg-red-50 text-red-700 border-red-200",
    immersion_scheduled: "bg-emerald-50 text-emerald-700 border-emerald-200",
    immersion_confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    immersion_cancelled: "bg-orange-50 text-orange-700 border-orange-200",
    validation_pending: "bg-purple-50 text-purple-700 border-purple-200",
    validation_done: "bg-purple-50 text-purple-700 border-purple-200",
    // Onboarding pipeline (iter 28)
    onboarding_sent: "bg-slate-50 text-slate-700 border-slate-200",
    onboarding_opened: "bg-sky-50 text-sky-700 border-sky-200",
    onboarding_submitted: "bg-indigo-50 text-indigo-700 border-indigo-200",
    onboarding_approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    onboarding_needs_fix: "bg-rose-50 text-rose-700 border-rose-200",
    onboarding_reminder: "bg-amber-50 text-amber-800 border-amber-200",
    // Contract pipeline (iter 28)
    contract_ready: "bg-slate-50 text-slate-700 border-slate-200",
    contract_sent: "bg-sky-50 text-sky-700 border-sky-200",
    contract_opened: "bg-indigo-50 text-indigo-700 border-indigo-200",
    contract_signed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    contract_voided: "bg-rose-50 text-rose-700 border-rose-200",
    contract_reminder: "bg-amber-50 text-amber-800 border-amber-200",
};

/**
 * Notifications dropdown panel. Click the bell to open / close. Renders
 * inline (popover) on desktop, drops down full-width on mobile.
 *
 * Auto-loads on open, marks notifications as read when the user clicks
 * one, and navigates them to the related candidate (if any).
 */
export default function NotificationsDropdown({ unreadCount, onUnreadChange }) {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const wrapRef = useRef(null);

    // Close on outside click / Escape
    useEffect(() => {
        if (!open) return;
        function onClick(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        }
        function onKey(e) {
            if (e.key === "Escape") setOpen(false);
        }
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    // Lock body scroll when the panel is open on mobile only (otherwise the
    // background can scroll behind the sheet)
    useEffect(() => {
        if (open && window.innerWidth < 1024) {
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = ""; };
        }
    }, [open]);

    async function fetchItems() {
        setLoading(true);
        try {
            const { data } = await api.get("/notifications");
            // Show only the 8 latest non-obsolete entries
            setItems(data.filter((n) => !n.is_obsolete).slice(0, 8));
        } catch (_e) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }

    function toggle() {
        const next = !open;
        setOpen(next);
        if (next) fetchItems();
    }

    async function handleClick(n) {
        // Mark as read (best-effort)
        if (!n.read) {
            try {
                await api.post(`/notifications/${n.id}/read`);
                onUnreadChange?.(Math.max(0, unreadCount - 1));
            } catch {
                /* ignore */
            }
        }
        setOpen(false);
        if (n.candidate_id) navigate(`/candidats/${n.candidate_id}`);
        else if (n.meta?.link) navigate(n.meta.link);
        else navigate("/notifications");
    }

    async function markAllRead() {
        try {
            await api.post("/notifications/read-all");
            onUnreadChange?.(0);
            // Reflect the change in the panel
            setItems((prev) => prev.map((n) => ({ ...n, read: true })));
        } catch {
            /* ignore */
        }
    }

    return (
        <div className="relative" ref={wrapRef}>
            <button
                type="button"
                onClick={toggle}
                data-testid="topbar-notifications"
                aria-label="Notifications"
                aria-expanded={open}
                className={`relative h-10 w-10 inline-flex items-center justify-center rounded-full border transition ${
                    open
                        ? "bg-shariti-orange-soft border-shariti-orange text-shariti-orange"
                        : "bg-white border-shariti-gray text-slate-500 hover:text-shariti-orange"
                }`}
            >
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-shariti-red text-white text-[10px] font-bold flex items-center justify-center">
                        {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <>
                        {/* Mobile: dim background to make the panel visually pop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="fixed inset-0 z-40 bg-black/30 lg:hidden"
                            onClick={() => setOpen(false)}
                            data-testid="notifications-overlay"
                        />
                        <motion.div
                            initial={{ opacity: 0, y: -8, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -8, scale: 0.98 }}
                            transition={{ duration: 0.18, ease: "easeOut" }}
                            data-testid="notifications-dropdown"
                            className="
                                fixed inset-x-3 top-[60px] z-50
                                lg:absolute lg:inset-x-auto lg:right-0 lg:top-12 lg:w-[400px]
                                bg-white rounded-2xl shadow-2xl border border-shariti-gray
                                h-[calc(100dvh-72px)] lg:h-auto lg:max-h-[80vh] flex flex-col overflow-hidden
                            "
                        >
                            <div className="px-4 py-3 border-b border-shariti-gray flex items-center justify-between">
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                        Notifications
                                    </div>
                                    <div className="text-sm font-extrabold text-shariti-black">
                                        {unreadCount === 0 ? "Tout est lu" : `${unreadCount} non lue${unreadCount > 1 ? "s" : ""}`}
                                    </div>
                                </div>
                                {unreadCount > 0 && (
                                    <button
                                        onClick={markAllRead}
                                        data-testid="dropdown-mark-all-read"
                                        className="inline-flex items-center gap-1 text-xs font-bold text-shariti-orange hover:underline"
                                    >
                                        <CheckCheck className="h-3.5 w-3.5" />
                                        Tout lire
                                    </button>
                                )}
                            </div>

                            <div className="flex-1 min-h-0 overflow-y-auto">
                                {loading ? (
                                    <div className="flex items-center justify-center py-10">
                                        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                                    </div>
                                ) : items.length === 0 ? (
                                    <div className="px-4 py-10 text-center text-sm font-medium text-slate-400">
                                        Aucune notification pour le moment.
                                    </div>
                                ) : (
                                    <ul className="divide-y divide-shariti-gray/60">
                                        {items.map((n) => {
                                            const accent = TYPE_ACCENT[n.type] || "bg-slate-50 text-slate-700 border-slate-200";
                                            const sourceIcon = n.candidate_offer_label?.startsWith("Candidature spontanée")
                                                ? Globe
                                                : Briefcase;
                                            const SourceIcon = sourceIcon;
                                            return (
                                                <li key={n.id}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleClick(n)}
                                                        data-testid={`notif-dropdown-item-${n.id}`}
                                                        className={`w-full text-left px-4 py-3 transition hover:bg-shariti-orange-soft/40 ${
                                                            !n.read ? "bg-shariti-orange-soft/20" : ""
                                                        }`}
                                                    >
                                                        <div className="flex items-start gap-2.5">
                                                            <span
                                                                className={`mt-0.5 inline-flex items-center justify-center h-5 w-5 rounded-full border text-[10px] font-bold shrink-0 ${accent}`}
                                                                aria-hidden
                                                            >
                                                                {!n.read ? "•" : ""}
                                                            </span>
                                                            <div className="min-w-0 flex-1">
                                                                <div className={`text-sm font-extrabold text-shariti-black ${n.is_obsolete ? "line-through opacity-50" : ""}`}>
                                                                    {n.title}
                                                                </div>
                                                                <div className="mt-0.5 text-xs text-slate-600 line-clamp-2">
                                                                    {n.message}
                                                                </div>
                                                                {n.candidate_offer_label && (
                                                                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-slate-500 max-w-full">
                                                                        <SourceIcon className="h-3 w-3 shrink-0" />
                                                                        <span className="truncate">{n.candidate_offer_label}</span>
                                                                    </div>
                                                                )}
                                                                <div className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                                                                    {timeAgo(n.created_at)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>

                            <div className="border-t border-shariti-gray px-4 py-2.5">
                                <button
                                    onClick={() => { setOpen(false); navigate("/notifications"); }}
                                    data-testid="notifications-dropdown-see-all"
                                    className="w-full inline-flex items-center justify-center gap-2 h-9 rounded-full bg-shariti-orange-soft text-shariti-orange hover:bg-shariti-orange hover:text-white border border-shariti-orange/30 transition font-extrabold text-xs"
                                >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    Voir toutes les notifications
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
